// Central helper — always render Experience event/slot timestamps in
// Europe/Paris regardless of the visitor's browser timezone. Also provides
// the inverse conversion when the organizer creates a slot from wall-clock
// date + time inputs.
const PARIS = "Europe/Paris";

export function formatParisDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      timeZone: PARIS,
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

export function formatParisDateShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      timeZone: PARIS, weekday: "short", day: "2-digit", month: "short",
    });
  } catch { return iso; }
}

export function formatParisTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("fr-FR", {
      timeZone: PARIS, hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function formatParisDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      timeZone: PARIS,
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// Convert an ISO UTC string into a `YYYY-MM-DDTHH:MM` string as it would be
// displayed in Europe/Paris — suitable for a `datetime-local` input value.
export function isoToParisDatetimeLocal(iso) {
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Paris",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso));
    const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
    const hour = p.hour === "24" ? "00" : p.hour;
    return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
  } catch { return ""; }
}

// Convert a `YYYY-MM-DDTHH:MM` value (from a `datetime-local` input) — read as
// Europe/Paris wall-clock — into a UTC ISO string.
export function parisDatetimeLocalToIso(value) {
  if (!value) return null;
  const [dateYMD, hhmm] = value.split("T");
  return parisWallToIso(dateYMD, (hhmm || "").slice(0, 5));
}

// Return the UTC ISO string that corresponds to the given wall-clock date
// (`YYYY-MM-DD`) and time (`HH:MM`) interpreted as Europe/Paris — regardless
// of the browser's own timezone. Used when the organizer creates slots so
// the intent ("10:00 Paris") stays stable even if they're travelling.
export function parisWallToIso(dateYMD, hhmm) {
  if (!dateYMD || !hhmm) return null;
  const [y, mo, d] = dateYMD.split("-").map(Number);
  const [h, mi] = hhmm.split(":").map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;
  // Guess: treat the wall clock as UTC.
  const guessUtc = Date.UTC(y, mo - 1, d, h, mi, 0);
  // What time would Paris show for that guessUtc instant?
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: PARIS,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(new Date(guessUtc));
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  const hour = p.hour === "24" ? "0" : p.hour;
  const parisEquivalentUtc = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(hour), Number(p.minute), Number(p.second),
  );
  const offsetMs = guessUtc - parisEquivalentUtc;
  return new Date(guessUtc + offsetMs).toISOString();
}
